import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Search, X, Loader2, User, Users, Book, Library, Sparkles, Wand2,
  Briefcase, LifeBuoy, Building2, CreditCard, ArrowRight } from 'lucide-react';

const CATEGORY_META = {
  children:       { label: 'My Kids',        icon: Users,       color: 'text-emerald-600 bg-emerald-50' },
  activities:     { label: 'Activities',     icon: Book,        color: 'text-blue-600 bg-blue-50' },
  bible_stories:  { label: 'Bible Stories',  icon: Sparkles,    color: 'text-amber-600 bg-amber-50' },
  summer_lessons: { label: 'Summer Lessons', icon: Book,        color: 'text-rose-600 bg-rose-50' },
  library:        { label: 'My Library',     icon: Library,     color: 'text-pink-600 bg-pink-50' },
  materials:      { label: 'Materials',      icon: Wand2,       color: 'text-purple-600 bg-purple-50' },
  billing:        { label: 'Billing',        icon: CreditCard,  color: 'text-slate-700 bg-slate-100' },
  users:          { label: 'Users',          icon: User,        color: 'text-indigo-600 bg-indigo-50' },
  organizations:  { label: 'Organizations',  icon: Building2,   color: 'text-cyan-600 bg-cyan-50' },
  employees:      { label: 'Team',           icon: Users,       color: 'text-teal-600 bg-teal-50' },
  careers:        { label: 'Careers',        icon: Briefcase,   color: 'text-orange-600 bg-orange-50' },
  applications:   { label: 'Applications',   icon: Briefcase,   color: 'text-amber-700 bg-amber-50' },
  support:        { label: 'Support Tickets',icon: LifeBuoy,    color: 'text-red-600 bg-red-50' },
};

const CATEGORY_ORDER = [
  'children', 'library', 'activities', 'bible_stories', 'summer_lessons',
  'materials', 'billing',
  'users', 'organizations', 'employees', 'careers', 'applications', 'support',
];

const GlobalSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Global keyboard shortcut: Cmd/Ctrl + K to open, Esc to close
  useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;
      if (modifier && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Autofocus when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setResult(null);
    }
  }, [open]);

  // Debounced search
  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResult(null);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
      setResult(res.data);
    } catch (err) {
      setResult({ groups: {}, total: 0, error: true });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const openHit = (hit) => {
    setOpen(false);
    if (hit.url) navigate(hit.url);
  };

  // Only render for signed-in users
  if (!user) return null;
  // Hide floating trigger on full-screen chat pages so it never blocks the composer
  const hideOnRoutes = ['/employee/chat', '/team/chat', '/team/meetings'];
  const hideTrigger = hideOnRoutes.some((p) => location.pathname.startsWith(p));

  const orderedGroups = result
    ? CATEGORY_ORDER.filter((k) => result.groups && result.groups[k] && result.groups[k].length > 0)
    : [];

  return (
    <>
      {/* Floating trigger button — bottom-right on all viewports (out of the way of dashboard cards) */}
      {!hideTrigger && (
      <button
        onClick={() => setOpen(true)}
        aria-label="Open global search"
        data-testid="global-search-trigger"
        className="fixed z-30 bottom-6 right-6 flex items-center gap-2 px-3 py-2 rounded-full bg-white/90 backdrop-blur border border-slate-200 shadow-md text-slate-600 hover:bg-white hover:shadow-lg transition-all text-sm font-medium"
      >
        <Search className="w-4 h-4" />
        <span className="hidden md:inline">Search</span>
        <kbd className="hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono text-slate-400 bg-slate-100 rounded border border-slate-200">
          ⌘K
        </kbd>
      </button>
      )}

      {/* Command palette overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="global-search-overlay"
        >
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
            {/* Input row */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
              <Search className="w-5 h-5 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search kids, lessons, bible stories, materials, payments…"
                className="flex-1 bg-transparent outline-none text-slate-800 placeholder:text-slate-400"
                data-testid="global-search-input"
              />
              {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-slate-100 text-slate-400"
                aria-label="Close search"
                data-testid="global-search-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Results body */}
            <div className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
              {!query && (
                <div className="p-8 text-center text-slate-500 text-sm">
                  <p>Type to search across everything you have access to.</p>
                  <p className="mt-1 text-xs text-slate-400">
                    Press <kbd className="px-1 py-0.5 bg-slate-100 rounded border">ESC</kbd> to close · <kbd className="px-1 py-0.5 bg-slate-100 rounded border">⌘K</kbd> to toggle
                  </p>
                </div>
              )}

              {query && !loading && result && result.total === 0 && (
                <div className="p-8 text-center text-slate-500 text-sm" data-testid="global-search-empty">
                  No matches for <span className="font-mono text-slate-700">&quot;{query}&quot;</span>.
                </div>
              )}

              {orderedGroups.map((groupKey) => {
                const meta = CATEGORY_META[groupKey] || { label: groupKey, icon: Search, color: 'text-slate-600 bg-slate-50' };
                const Icon = meta.icon;
                return (
                  <div key={groupKey} className="border-t border-slate-100 first:border-t-0">
                    <div className="px-4 py-2 flex items-center gap-2 bg-slate-50/60 text-xs font-bold uppercase tracking-wider text-slate-500">
                      <div className={`w-5 h-5 rounded flex items-center justify-center ${meta.color}`}>
                        <Icon className="w-3 h-3" />
                      </div>
                      {meta.label}
                      <span className="text-slate-400 font-normal ml-auto">
                        {result.groups[groupKey].length}
                      </span>
                    </div>
                    {result.groups[groupKey].map((hit) => (
                      <button
                        key={`${groupKey}-${hit.id}`}
                        onClick={() => openHit(hit)}
                        className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 text-left transition-colors group"
                        data-testid={`global-search-hit-${groupKey}-${hit.id}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.color} flex-shrink-0`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{hit.title}</p>
                          {hit.subtitle && (
                            <p className="text-xs text-slate-500 truncate">{hit.subtitle}</p>
                          )}
                        </div>
                        {hit.badge && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                            {hit.badge}
                          </span>
                        )}
                        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-600 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50 text-[11px] text-slate-500 flex items-center justify-between">
              <span>{result?.scope === 'admin' ? 'Admin search' : 'Parent search'}</span>
              <span>
                <kbd className="px-1 py-0.5 bg-white rounded border">↑↓</kbd> navigate · <kbd className="px-1 py-0.5 bg-white rounded border">↵</kbd> open
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearch;
